import { useMutation } from '@/hooks/useApi';
import * as api from '@/services/api';
import { getCapaWorkflow, processCapa } from '@/services/api/audit-ai';

interface RetryArgs {
  // The failed CAPA workflow id (AUDIT item's `assetId`).
  workflowId: string;
  // The AI-processing-status row id to clear once the retry starts.
  itemId: string;
}

/**
 * Retries a failed AUDIT job from the queue using the SAME mechanism as the
 * audit-AI dialog's retry: re-trigger `processCapa`. The original file ids
 * aren't on the summary item, so we read them off the failed workflow first
 * (`getCapaWorkflow` exposes `auditFileId`/`capaFileId`). `processCapa` starts a
 * fresh workflow + AI-processing row, so we dismiss the stale failed row to
 * avoid a duplicate. Audit-only — other asset types have no retry today.
 */
export function useRetryAuditProcessing() {
  return useMutation({
    mutationFn: async ({ workflowId, itemId }: RetryArgs) => {
      const workflow = await getCapaWorkflow(workflowId);
      await processCapa({
        auditFileId: workflow.auditFileId ?? undefined,
        capFileId: workflow.capaFileId ?? undefined,
        // Carry the CAP-import context over to the fresh workflow. Without it a
        // retried CAP-only import loses the audit/company/plan it was started
        // for, so its new queue card can no longer be resumed into a review.
        importContext: workflow.metadata?.importContext,
      });
      await api.aiProcessing.dismissAiProcessingItem(itemId);
    },
  });
}
